import { defineStore } from 'pinia'
import { ref } from 'vue'
import type egresses from '@/backend/egresses'
import type ingresses from '@/backend/ingresses'
import type { Egress } from '@/backend/egresses.types'
import type { Ingress } from '@/backend/ingresses.types'

export interface ResourcesServices {
  egressesService: ReturnType<typeof egresses>
  ingressesService: ReturnType<typeof ingresses>
}

/**
 * The tenant's ingress and egress resources, cached.
 *
 * A plain store with no factory, deliberately: these lists are tenant-wide and single-instance, so
 * one shared copy is the entire point. That is what distinguishes a cache from view state — anything
 * a view can hold two different values of at once belongs in a view context instead, or two open
 * tabs will fight over it.
 *
 * Read-mostly. The per-resource logging overrides that Monitoring edits live in its own view
 * context; this store only serves the resource lists and is invalidated after a write.
 */
export const useResourcesStore = defineStore('resources', () => {
  const ingresses = ref<Ingress[]>([])
  const egresses = ref<Egress[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  let loaded = false

  /**
   * Fetches both families once. Subsequent calls resolve immediately unless `force` is set, so
   * several views mounting together share one round trip.
   */
  async function load(services: ResourcesServices, force = false): Promise<void> {
    if (loaded && !force) return

    isLoading.value = true
    error.value = null

    const failures: string[] = []

    // Both families in parallel, and a failure in one does not discard the other: a grid spanning
    // both would otherwise look as though the missing family had no resources at all.
    await Promise.all([
      (async () => {
        try {
          ingresses.value = await services.ingressesService.getAll()
        } catch (e) {
          console.error('Failed to load ingresses:', e)
          failures.push('ingresses')
        }
      })(),
      (async () => {
        try {
          egresses.value = await services.egressesService.getAll()
        } catch (e) {
          console.error('Failed to load egresses:', e)
          failures.push('egresses')
        }
      })(),
    ])

    isLoading.value = false
    loaded = true

    if (failures.length > 0) {
      error.value = `Failed to load: ${failures.join(', ')}`
    }
  }

  /** Forces the next `load` to re-fetch — call after writing a resource. */
  function invalidate(): void {
    loaded = false
  }

  return {
    ingresses,
    egresses,
    isLoading,
    error,
    load,
    invalidate,
  }
})
